import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import { ProjectAccStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_projectManagementProjectDetailsForm.module.scss';
import { useAppDispatch } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import { TEditProjectDetailsReduxParams, TProjectDetails, TEditProjectDetailsValidation } from '~types/feat/project';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { toast } from 'react-toastify';

type TProjectManagementProjectDetailsForm = {
    submitButtonLabel: string;
    onSubmit: (data: TEditProjectDetailsReduxParams) => void;
    error: string;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
    initialProjectDetails?: TProjectDetails;
}

const ProjectManagementProjectDetailsForm: FC<TProjectManagementProjectDetailsForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    isEditing,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
    initialProjectDetails,
}) => {
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const projectManagementProjectDetailsFormValidation = Yup.object().shape({
        projectName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().validateProjectName().label('Project Name').required('Project Name is required'),
        earmarkAmount: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Earmark quantity is required')
            .numericPositive('Earmark quantity')
            .label('Earmark Quantity'),

        earmarkEndDate: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Maturity period is required')
            .numericPositive('Maturity period')
            .label('Maturity period'),
        status: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(ProjectAccStatusEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Status').required('Status is required'),

    });

    const projectManagementProjectDetailsFormInitialValues: TEditProjectDetailsValidation = {
        projectName: initialProjectDetails?.projectName || '',
        earmarkAmount: initialProjectDetails?.earmarkAmount || '',
        earmarkEndDate: initialProjectDetails?.earmarkEndDate || '',
        description: initialProjectDetails?.description || '',
        status: initialProjectDetails?.status ? ProjectAccStatusEnum[initialProjectDetails?.status] as keyof typeof ProjectAccStatusEnum : '',
    };

    const formik = useFormik({
        initialValues: projectManagementProjectDetailsFormInitialValues,
        validationSchema: projectManagementProjectDetailsFormValidation,
        onSubmit: (values) => {
            if (!initialProjectDetails?.projectId) return;

            onSubmit({
                projectId: initialProjectDetails.projectId,
                projectName: values.projectName,
                description: values.description || '',
                earmarkAmount: values.earmarkAmount,
                earmarkEndDate: values.earmarkEndDate,
                status: ProjectAccStatusEnum[values.status as keyof typeof ProjectAccStatusEnum],
            });
        },

    });

    useEffect(() => {
        if (!isEditing) {
            formik.resetForm();
        }
    }, [isEditing]);

    const statusOptions = Object.values(ProjectAccStatusEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Reset the form if the status is changed to not deactivated and changed back to deactivated (not allowed to update details if status is deactivated)
        if (e.target.value === ProjectAccStatusEnum[ProjectAccStatusEnum.Deactivated]
                && initialProjectDetails?.status === ProjectAccStatusEnum.Deactivated
                && formik.values.status !== ProjectAccStatusEnum[ProjectAccStatusEnum.Deactivated]
        ) {
            toast.info('Form is reset');
            formik.resetForm();
        }

        formik.setFieldValue('status', e.target.value);
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='projectName'
                    name='projectName'
                    label='Project Name* :'
                    value={formik.values.projectName}
                    error={formik.touched.projectName ? formik.errors.projectName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={FormStyles['span-2']}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='earmarkAmount'
                    name='earmarkAmount'
                    label='Earmark Quantity (RM)* :'
                    value={formik.values.earmarkAmount}
                    error={formik.touched.earmarkAmount ? formik.errors.earmarkAmount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='earmarkEndDate'
                    name='earmarkEndDate'
                    label='Maturity Period (Years)* :'
                    value={formik.values.earmarkEndDate}
                    error={formik.touched.earmarkEndDate ? formik.errors.earmarkEndDate : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='status'
                    name='status'
                    label='Status* :'
                    value={String(formik.values.status)}
                    error={formik.touched.status ? formik.errors.status : undefined}
                    onChange={handleStatusChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                    options={statusOptions}
                    autoComplete='off'
                    optionSelected={!!statusOptions.find(option => option.value === formik.values.status)}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled || !formik.dirty}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default ProjectManagementProjectDetailsForm;
